import { Dispatch, SetStateAction, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Search } from "@mui/icons-material"
import {
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Box,
} from "@mui/material"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { BillReceiversItem } from "../../../accounting/bill-receivers/billReceiversTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    AccountingBillReceiversResponse,
    BillReceiversQuery,
    fetchAccountingBillReceivers,
} from "../../../accounting/bill-receivers/billReceiversApi"
import { BillReceiverType } from "../userGroupsTypes"
import { SelectionDialog } from "./Elements"

const useStyles = makeStyles()((theme) => ({
    infoText: {
        color: theme.palette.tailwind.slate[300],
    },
    orText: {
        color: theme.palette.tailwind.slate[300],
        margin: theme.spacing(0, 2),
        textTransform: "lowercase",
    },
    listItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.25rem",
        color: theme.palette.tailwind.slate[600],
        padding: "1rem",
        marginBottom: "0.75rem",
        cursor: "pointer",
        "&:last-child": {
            marginBottom: 0,
        },
    },
    listItemNumbers: {
        display: "flex",
        gap: "1rem",
    },
    listItemTitle: {
        color: theme.palette.tailwind.slate[900],
    },
    dividerContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: theme.spacing(1, 0),
    },
    divider: {
        width: 80,
    },
    createButton: {
        width: "fit-content",
        alignSelf: "center",
    },
}))

type AddBillReceiverProps = Readonly<{
    type: "new" | "update"
    searchText?: string
    companyId: number
    handleChooseBillReceiver: (receiver: BillReceiversItem) => void
    dialogState: boolean
    setDialogState: Dispatch<SetStateAction<boolean>>
    handleCreateNewBill: () => void
}>

const selectBillReceivers = (data: AccountingBillReceiversResponse) => data.billReceivers

export const AddBillReceiver = ({
    type,
    searchText = "",
    companyId,
    handleChooseBillReceiver,
    dialogState,
    setDialogState,
    handleCreateNewBill,
}: AddBillReceiverProps) => {
    const { classes, cx } = useStyles()

    const [search, setSearch] = useState(searchText)
    const [selectedItem, setSelectedItem] = useState<string | undefined>()
    const searchRegex = useMemo(() => (search ? new RegExp(`(${search})`, "i") : undefined), [search])

    const { data = [] } = useApiQuery(fetchAccountingBillReceivers, {
        queryName: BillReceiversQuery.LIST,
        dependencies: {
            companyId,
        },
        select: selectBillReceivers,
    })

    const filteredBillReceivers = useMemo(() => {
        if (!searchRegex) return data || []
        return (data || []).filter(
            (receiver) =>
                searchRegex.test(receiver.name) ||
                (receiver?.cvrNumber && searchRegex.test(receiver.cvrNumber)) ||
                (receiver.endpoint.type === BillReceiverType.EAN_NUMBER
                    ? searchRegex.test(receiver.endpoint?.eanNumber)
                    : searchRegex.test(receiver.endpoint?.email)) ||
                searchRegex.test(receiver.addressDisplayText),
        )
    }, [data, searchRegex])

    const findReceiverById = (id: string, cb: AddBillReceiverProps["handleChooseBillReceiver"]) => {
        const selectedReceiver = filteredBillReceivers.find((receiver) => String(receiver.id) === id)
        if (selectedReceiver) cb(selectedReceiver)
    }

    const handleChange = (event: SelectChangeEvent) => findReceiverById(event.target.value, handleChooseBillReceiver)
    const onChoose = () => selectedItem && findReceiverById(selectedItem, handleChooseBillReceiver)

    return (
        <LocalizedStrict
            id={
                type === "new"
                    ? "create-or-edit-company-user-group-connected-bill-receiver-modal-add-bill-receiver"
                    : "create-or-edit-company-user-group-connected-bill-receiver-modal-update-bill-receiver"
            }
            attrs={{ title: type === "new" }}
        >
            <SelectionDialog
                dialogState={dialogState}
                setDialogState={setDialogState}
                title={type === "new" ? "Add bill receiver" : ""}
            >
                <Grid container gap={3} flexDirection="column">
                    {type === "new" && (
                        <LocalizedStrict
                            id="create-or-edit-company-user-group-connected-bill-receiver-modal-field-search"
                            attrs={{ label: true, placeholder: true }}
                        >
                            <TextField
                                name="search"
                                value={search}
                                fullWidth
                                label="Search"
                                placeholder="Search by name, CVR, EAN, email or address"
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </LocalizedStrict>
                    )}
                    {type === "update" && (
                        <LocalizedStrict
                            id="create-or-edit-company-user-group-connected-bill-receiver-modal-bill-found"
                            vars={{ count: filteredBillReceivers.length }}
                        >
                            <Typography variant="h5" textAlign="center" fontWeight={700}>
                                {filteredBillReceivers.length} bill receivers already exists with the same identifier.
                            </Typography>
                        </LocalizedStrict>
                    )}

                    {search ? (
                        filteredBillReceivers?.length ? (
                            <>
                                <LocalizedStrict id="bill-receivers-create-dialog-select-bill-receiver">
                                    <Typography variant="subtitle1" textAlign="start">
                                        Select existing bill receiver:
                                    </Typography>
                                </LocalizedStrict>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="bill-receivers-select-list">
                                        <LocalizedStrict id="bill-receivers-select-list-label">
                                            <>Select bill receiver</>
                                        </LocalizedStrict>
                                    </InputLabel>
                                    <Select
                                        labelId="bill-receivers-select-list"
                                        id="bill-receivers-select-label"
                                        value={String(selectedItem)}
                                        onChange={handleChange}
                                        label="Select bill receiver"
                                        placeholder="Select bill receiver"
                                    >
                                        {filteredBillReceivers.map((receiver) => (
                                            <MenuItem
                                                key={receiver.id}
                                                value={String(receiver.id)}
                                                className={classes.listItem}
                                            >
                                                <Typography variant="subtitle2">{receiver.name}</Typography>
                                                <Box className={classes.listItemNumbers}>
                                                    {receiver?.cvrNumber && (
                                                        <Typography variant="body2">
                                                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-cvr">
                                                                <>CVR:</>
                                                            </LocalizedStrict>{" "}
                                                            {receiver?.cvrNumber}
                                                        </Typography>
                                                    )}

                                                    {receiver.endpoint.type === BillReceiverType.EAN_NUMBER ? (
                                                        <Typography variant="body2">
                                                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-ean">
                                                                <>EAN:</>
                                                            </LocalizedStrict>{" "}
                                                            {receiver.endpoint.eanNumber}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-email">
                                                                <>Email:</>
                                                            </LocalizedStrict>{" "}
                                                            {receiver.endpoint.email}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {receiver.addressDisplayText && (
                                                    <Typography variant="body2">
                                                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-address">
                                                            <>Address:</>
                                                        </LocalizedStrict>{" "}
                                                        {receiver.addressDisplayText}
                                                    </Typography>
                                                )}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        ) : (
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-empty">
                                <Typography
                                    className={classes.infoText}
                                    variant="body2"
                                    textAlign="center"
                                    fontStyle="italic"
                                >
                                    No search results found
                                </Typography>
                            </LocalizedStrict>
                        )
                    ) : (
                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-info-text">
                            <Typography
                                className={classes.infoText}
                                variant="body2"
                                textAlign="center"
                                fontStyle="italic"
                            >
                                Start typing to show search results
                            </Typography>
                        </LocalizedStrict>
                    )}
                    <Grid container gap={2} flexDirection="column">
                        {type === "new" && (
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-submit-button">
                                <StandardButton onClick={onChoose} disabled={!selectedItem}>
                                    Choose selected bill receiver
                                </StandardButton>
                            </LocalizedStrict>
                        )}
                        <Box className={classes.dividerContainer}>
                            <Divider className={classes.divider} />
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-or">
                                <Typography className={classes.orText} variant="body2" textAlign="center">
                                    or
                                </Typography>
                            </LocalizedStrict>
                            <Divider className={classes.divider} />
                        </Box>
                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-create-button">
                            <StandardButton
                                color="blue"
                                variant="outlined"
                                onClick={handleCreateNewBill}
                                updateDefaultClasses={classes.createButton}
                            >
                                Create new bill receiver
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-info-warning">
                            <Typography variant="body2" className={classes.infoText} textAlign="center">
                                You should create a new bill receiver with the same identifier (CVR, EAN, email) only if
                                the contact details differ. For example, this may be necessary if the bill needs to be
                                directed to a different individual.
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </SelectionDialog>
        </LocalizedStrict>
    )
}
